import { useState, useEffect, useRef } from 'react';
import { collection, onSnapshot, query, orderBy, doc, getDoc, setDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { User } from 'firebase/auth';
import { db, getFirestoreDb } from '../firebase/firebase';
import { WaitlistEntry, UserProfile } from '@shared/types';
import { getWaitlistDisplayNames } from '@shared/utils/firestoreHelpers';

interface UseWaitlistOptions {
  enabled?: boolean;
}

interface UseWaitlistResult {
  waitlist: WaitlistEntry[];
  waitlistCount: number;
  isUserOnWaitlist: boolean;
  joinWaitlist: () => Promise<void>;
  leaveWaitlist: () => Promise<void>;
  loading: boolean;
  error: Error | null;
  isJoining: boolean;
  isLeaving: boolean;
  displayNames: Map<string, string>;
}

export function useWaitlist(
  roomId: string,
  tableId: string,
  user: User | null,
  userProfile: UserProfile | null,
  options: UseWaitlistOptions = {}
): UseWaitlistResult {
  const { enabled = true } = options;
  const [waitlist, setWaitlist] = useState<WaitlistEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [isJoining, setIsJoining] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);
  const unsubscribeRef = useRef<(() => void) | null>(null);
  const [displayNames, setDisplayNames] = useState<Map<string, string>>(new Map());

  useEffect(() => {
    if (!roomId || !tableId || !enabled) {
      setWaitlist([]);
      setLoading(false);
      setError(null);
      return;
    }

    if (!db) {
      setError(new Error('Firebase not initialized'));
      setLoading(false);
      return;
    }

    try {
      // Create waitlist collection reference directly
      const waitlistRef = collection(db, 'rooms', roomId, 'tables', tableId, 'waitlist');
      
      setLoading(true);
      setError(null);

      // Try position query first, fallback to added_at
      let q = query(waitlistRef, orderBy('position', 'asc'));
      
      const unsubscribe = onSnapshot(
        q,
        async (snapshot) => {
          const waitlistData = snapshot.docs.map(doc => {
            const data = doc.data();
            // Ensure position field exists, default to 0 if missing
            if (data.position === undefined) {
              data.position = 0;
            }
            return { id: doc.id, ...data } as WaitlistEntry;
          });
          
          // Fetch current display names (aliases or names) for all entries
          try {
            if (db && waitlistData.length > 0) {
              const entriesWithDisplay = await getWaitlistDisplayNames(db, waitlistData);
              const nameMap = new Map<string, string>();
              entriesWithDisplay.forEach(entry => {
                if (entry.displayName) {
                  nameMap.set(entry.id, entry.displayName);
                }
              });
              setDisplayNames(nameMap);
            }
          } catch (err) {
            console.error('Error fetching display names:', err);
            // Continue without display names - will fall back to entry.name
          }
          
          setWaitlist(waitlistData);
          setLoading(false);
        },
        (_err) => {
          // If position query fails, try added_at query
          const fallbackQuery = query(waitlistRef, orderBy('added_at', 'asc'));
          const fallbackUnsubscribe = onSnapshot(
            fallbackQuery,
            (snapshot) => {
              const waitlistData = snapshot.docs.map(doc => {
                const data = doc.data();
                if (data.position === undefined) {
                  data.position = 0;
                }
                return { id: doc.id, ...data } as WaitlistEntry;
              });
              setWaitlist(waitlistData);
              setLoading(false);
            },
            (fallbackErr) => {
              setError(fallbackErr as Error);
              setLoading(false);
            }
          );
          
          // Store the fallback unsubscribe function
          unsubscribeRef.current = fallbackUnsubscribe;
        }
      );

      unsubscribeRef.current = unsubscribe;

      return () => {
        if (unsubscribeRef.current) {
          unsubscribeRef.current();
          unsubscribeRef.current = null;
        }
      };
    } catch (err) {
      setError(err as Error);
      setLoading(false);
      return;
    }
  }, [roomId, tableId, enabled]);

  // Calculate derived state
  const waitlistCount = waitlist.filter(entry => entry.status === 'waiting').length;
  const isUserOnWaitlist = user ? waitlist.some(entry => entry.user_id === user.uid && entry.status === 'waiting') : false;

  const joinWaitlist = async () => {
    if (!user || !userProfile || isJoining || isLeaving) {
      return;
    }
    setIsJoining(true);
    setError(null);
    try {
      // Fetch the latest user profile from Firestore to ensure we have the most current alias/name
      const freshDb = getFirestoreDb();
      const userRef = doc(freshDb, 'users', user.uid);
      const userProfileSnapshot = await getDoc(userRef);
      
      // Use fresh profile data if available, otherwise fall back to prop value
      let freshProfile: UserProfile | null = null;
      if (userProfileSnapshot.exists()) {
        freshProfile = { uid: userProfileSnapshot.id, ...userProfileSnapshot.data() } as UserProfile;
      }
      
      // Use fresh profile data for display name and phone, fall back to prop if fetch failed
      const profileToUse = freshProfile || userProfile;
      const displayName = profileToUse.alias && profileToUse.alias.trim() !== '' 
        ? profileToUse.alias 
        : profileToUse.name;
      
      const waitlistEntryRef = doc(db, 'rooms', roomId, 'tables', tableId, 'waitlist', user.uid);
      const waitingEntries = waitlist.filter(entry => entry.status === 'waiting');
      const newPosition = waitingEntries.length > 0 ? Math.max(...waitingEntries.map(e => e.position || 0)) + 1 : 1;

      // Create waitlist entry data with fresh profile information
      const waitlistData: any = {
        user_id: user.uid,
        name: displayName,
        position: newPosition,
        status: 'waiting',
        added_at: serverTimestamp(),
        added_by: 'self',
      };

      // Only add phone if it's not undefined
      if (profileToUse.phone !== undefined && profileToUse.phone !== null) {
        waitlistData.phone = profileToUse.phone;
      }

        await setDoc(waitlistEntryRef, waitlistData);
    } catch (err) {
      setError(err as Error);
    } finally {
      setIsJoining(false);
    }
  };

  const leaveWaitlist = async () => {
    if (!user || isJoining || isLeaving) return;
    setIsLeaving(true);
    setError(null);
    try {
      // Prefer removing any existing entries for this user (handles legacy docs not keyed by uid)
      const userEntries = waitlist.filter(
        (entry) => entry.user_id === user.uid && entry.status === 'waiting'
      );

      if (userEntries.length > 0) {
        // Delete all matching entries to ensure a clean state
        await Promise.all(
          userEntries.map((entry) =>
            deleteDoc(doc(db, 'rooms', roomId, 'tables', tableId, 'waitlist', entry.id))
          )
        );
      } else {
        // Fallback: delete by UID (current write strategy)
        const waitlistEntryRef = doc(db, 'rooms', roomId, 'tables', tableId, 'waitlist', user.uid);
        await deleteDoc(waitlistEntryRef);
      }
    } catch (err) {
      setError(err as Error);
    } finally {
      setIsLeaving(false);
    }
  };

  return {
    waitlist,
    waitlistCount,
    isUserOnWaitlist,
    joinWaitlist,
    leaveWaitlist,
    loading,
    error,
    isJoining,
    isLeaving,
    displayNames,
  };
}
